import type { PolicyType } from './policy-management/policy-type'
import type {
	PolicyCategory,
	PolicySeverity,
} from '@/features/air/policy-management/types'

/**
 * The display layer for a policy's three closed axes: **type**, **severity**
 * and **category**. One record per axis, imported by every surface that lists
 * or renders it — the tables, the filter dropdowns, the custom-policy editor's
 * selects, and the form schema's `z.enum`.
 *
 * Each axis is an exhaustive `Record<Union, …>`, so adding a member to the
 * backend enum is a compile error here rather than a value that silently never
 * appears in a filter (ADR 0020 §5).
 *
 * Filter lists are derived, never scraped from the rows on screen. Scraping
 * means "filter to Sigma" disappears from the dropdown on a page that happens
 * to have no Sigma rows — which is exactly when an operator reaches for it. On
 * case management it was worse: the options came from the **loaded pages of an
 * infinite query**, so the dropdown grew as the user scrolled.
 *
 * A filter option's `value` is always the **raw wire value**: it becomes the
 * `?filters[…]=` URL param and is matched against what the column accessor
 * produces, so it cannot be the label. All three backing columns are `VARCHAR`
 * with the enum enforced in application code only, so a row carrying an
 * unlisted value still renders — it simply has no dropdown entry.
 */

// ── Type — what kind of rule this is ────────────────────────

/**
 * `PolicyType`. **Not** the policy's category: this says where the rule came
 * from (BitLyft's essential set, the Sigma corpus, or the tenant's own), while
 * {@link POLICY_CATEGORY_FILTER_OPTIONS} says what kind of data it inspects.
 * The two do not correlate, and conflating them is the trap — the filters
 * labelled "Category" filtered on *this* axis until they were relabelled.
 *
 * A case's `alert_type` is this same union: the policy type is carried through
 * at case creation (`alert["alert_type"] = policy.type` in the backend).
 *
 * Labels agree with `toDisplayName`, which the table cells use — including its
 * `bitlyft_essential → 'BitLyft Essential'` override. A test pins the agreement.
 */
const POLICY_TYPE_LABELS: Record<PolicyType, string> = {
	bitlyft_essential: 'BitLyft Essential',
	sigma: 'Sigma',
	custom: 'Custom',
}

/** The tuple form, for a `z.enum` over the policy types. */
export const POLICY_TYPES = Object.keys(POLICY_TYPE_LABELS) as [
	PolicyType,
	...Array<PolicyType>,
]

export const POLICY_TYPE_FILTER_OPTIONS = POLICY_TYPES.map((value) => ({
	value,
	label: POLICY_TYPE_LABELS[value],
}))

// ── Severity ────────────────────────────────────────────────

/**
 * `PolicySeverity` — the same three tiers `Severity` renders. Labels agree with
 * `resolveSeverity`, which owns severity's display spelling; a test pins it.
 */
const POLICY_SEVERITY_LABELS: Record<PolicySeverity, string> = {
	low: 'Low',
	medium: 'Medium',
	high: 'High',
}

/** The tuple form, for the custom-policy editor's `z.enum`. */
export const POLICY_SEVERITIES = [
	'low',
	'medium',
	'high',
] as const satisfies ReadonlyArray<PolicySeverity>

export const POLICY_SEVERITY_FILTER_OPTIONS = (
	Object.keys(POLICY_SEVERITY_LABELS) as Array<PolicySeverity>
).map((value) => ({ value, label: POLICY_SEVERITY_LABELS[value] }))

// ── Category — what kind of data the rule inspects ──────────

/**
 * `PolicyCategory`, all seven members.
 *
 * Two labels per member, deliberately. The editor's select is a form field with
 * room to explain itself, so it gets the `full` phrasing the product authored
 * ("Identity & Account Compromise"). A table column and a filter dropdown have
 * neither the width nor the need, so they get `short` ("Identity"). One record
 * owns both, so the two renderings cannot drift into different vocabularies —
 * which is what happened when this union had three parallel lists (the type
 * union, the form schema's tuple, and the editor's select items).
 *
 * The field is nullable on the wire (`Policy.category?: PolicyCategory | null`)
 * — an older policy predates it — so anything rendering it needs an absent
 * branch. See {@link policyCategoryLabel}.
 */
const POLICY_CATEGORY_LABELS: Record<
	PolicyCategory,
	{ short: string; full: string }
> = {
	endpoint: { short: 'Endpoint', full: 'Endpoint & Malware Threats' },
	identity: { short: 'Identity', full: 'Identity & Account Compromise' },
	email: { short: 'Email', full: 'Email Security & Messaging Abuse' },
	privilege: { short: 'Privilege', full: 'Privilege & Access Control Changes' },
	data_access: {
		short: 'Data Access',
		full: 'Data Access, Exfiltration & Compliance',
	},
	configuration_changes: {
		short: 'Configuration Changes',
		full: 'Cloud App & Identity Configuration Changes',
	},
	custom: { short: 'Custom', full: 'Custom' },
}

/**
 * The tuple form, for the custom-policy editor's `z.enum`, which needs literal
 * types rather than a widened `Array<PolicyCategory>`. A test pins it against
 * {@link POLICY_CATEGORY_LABELS} so the two cannot fall out of step — the
 * `satisfies` alone would catch a *wrong* member but not a *missing* one.
 */
export const POLICY_CATEGORIES = [
	'endpoint',
	'identity',
	'email',
	'privilege',
	'data_access',
	'configuration_changes',
	'custom',
] as const satisfies ReadonlyArray<PolicyCategory>

export const POLICY_CATEGORY_FILTER_OPTIONS = POLICY_CATEGORIES.map(
	(value) => ({
		value,
		label: POLICY_CATEGORY_LABELS[value].short,
	}),
)

/** The editor's category select — the long phrasing, same record. */
export const POLICY_CATEGORY_SELECT_ITEMS = POLICY_CATEGORIES.map((value) => ({
	value,
	label: POLICY_CATEGORY_LABELS[value].full,
}))

/**
 * The short label for a category read off the wire, which may be absent (an
 * older policy) or — since the column is `VARCHAR` — a value this build does
 * not know. Absent returns `null` so the caller renders its own placeholder;
 * an unrecognised value returns the raw string rather than nothing (P-17).
 */
export const policyCategoryLabel = (
	category: string | null | undefined,
): string | null => {
	if (!category) return null
	return POLICY_CATEGORY_LABELS[category as PolicyCategory]?.short ?? category
}
